import type { AvailableSlot, BookingResult, MeetingType } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function schedulerFetch(action: string, params: Record<string, string> = {}, options?: RequestInit) {
  const url = new URL(`${SUPABASE_URL}/functions/v1/scheduler`);
  url.searchParams.set('action', action);
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v);
  }

  const res = await fetch(url.toString(), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_KEY,
      ...(options?.headers || {}),
    },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export async function fetchAvailability(
  meetingTypeSlug: string,
  timezone: string,
  dateRangeStart: string,
  dateRangeEnd: string
): Promise<{ slots: AvailableSlot[]; meetingType: MeetingType }> {
  return schedulerFetch('availability', {
    meetingTypeSlug,
    timezone,
    dateRangeStart,
    dateRangeEnd,
  });
}

export async function createBooking(data: {
  meetingTypeSlug: string;
  selectedSlotStart: string;
  firstName: string;
  lastName: string;
  email: string;
  organization?: string;
  role?: string;
  notes?: string;
  timezone: string;
  demo?: boolean;
}): Promise<BookingResult> {
  return schedulerFetch('book', {}, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function cancelBooking(token: string): Promise<{ success: boolean }> {
  return schedulerFetch('cancel', { token });
}

export async function rescheduleBooking(
  rescheduleToken: string,
  newSlotStart: string
): Promise<{ success: boolean }> {
  return schedulerFetch('reschedule', {}, {
    method: 'POST',
    body: JSON.stringify({ rescheduleToken, newSlotStart }),
  });
}

export async function getBooking(token: string, type: 'cancel' | 'reschedule' = 'cancel') {
  return schedulerFetch('get-booking', { token, type });
}

export function downloadICS(icsContent: string, filename = 'link-meeting.ics') {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
